import {
  useCallback,
  useEffect,
  useState,
  Dispatch,
  SetStateAction,
  useMemo,
} from 'react'

type LocalStorageCustomEventDetail = {
  key: string
}
type LocalStorageCustomEvent = CustomEvent<LocalStorageCustomEventDetail>

declare global {
  interface WindowEventMap {
    'local-storage': LocalStorageCustomEvent
  }
}

type UseLocalStorageOptions<T> = {
  /** A function to serialize the value before storing it. */
  serializer?: (value: T) => string
  /** A function to deserializer the stored value. */
  deserializer?: (value: string) => T
}

/**
 * useLocalStorage hook that automatically updates when localStorage is changed from another tab.
 *
 * @param key - The localStorage key
 * @param defaultValue - The initial value to use if no value exists in localStorage
 * @param options - Optional configuration for custom serialization
 * @returns A tuple with [value, setValue, removeValue] matching react-use's useLocalStorage
 */
export function useLocalStorage<T>(
  key: string,
  defaultValue: T | (() => T),
  options?: UseLocalStorageOptions<T>
): [T, Dispatch<SetStateAction<T>>, () => void] {
  // Resolve the initial value only once per key
  const defaultValueMemoized = useMemo(() => {
    return defaultValue instanceof Function ? defaultValue() : defaultValue
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]) // Re-run when key changes

  const customSerializer = options?.serializer
  const serializer = useCallback<(value: T) => string>(
    value => {
      if (customSerializer) {
        return customSerializer(value)
      }
      return JSON.stringify(value)
    },
    [customSerializer]
  )

  const customDeserializer = options?.deserializer
  const deserializer = useCallback<(value: string) => T>(
    value => {
      if (customDeserializer) {
        return customDeserializer(value)
      }

      let parsed: unknown
      try {
        parsed = JSON.parse(value)
      } catch (error) {
        return defaultValueMemoized // Return initialValue if parsing fails
      }

      return parsed as T
    },
    [customDeserializer, defaultValueMemoized]
  )

  // Get from local storage then parse stored json or return initialValue
  const readValue = useCallback((): T => {
    try {
      const raw = window.localStorage.getItem(key)
      return raw ? deserializer(raw) : defaultValueMemoized
    } catch (error) {
      return defaultValueMemoized
    }
  }, [defaultValueMemoized, key, deserializer])

  const [storedValue, setStoredValue] = useState<T>(() => {
    return readValue()
  })

  // Return a wrapped version of useState's setter function that persists the new value to localStorage
  const setValue: Dispatch<SetStateAction<T>> = useCallback(
    value => {
      try {
        // Allow value to be a function so we have the same API as useState
        const newValue = value instanceof Function ? value(readValue()) : value

        // Save to local storage
        window.localStorage.setItem(key, serializer(newValue))

        // Save state
        setStoredValue(newValue)

        // We dispatch a custom event so every similar useLocalStorage hook is notified
        window.dispatchEvent(
          new CustomEvent('local-storage', { detail: { key } })
        )
      } catch (error) {
        // Handle errors silently to match react-use behavior
      }
    },
    [key, serializer, readValue]
  )

  const removeValue = useCallback(() => {
    // Remove the key from local storage
    window.localStorage.removeItem(key)

    // Save state with default value
    setStoredValue(defaultValueMemoized)

    // We dispatch a custom event so every similar useLocalStorage hook is notified
    window.dispatchEvent(new CustomEvent('local-storage', { detail: { key } }))
  }, [key, defaultValueMemoized])

  useEffect(() => {
    setStoredValue(readValue())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  // Listen for changes to this localStorage key from other tabs (standard storage event)
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key !== key) {
        return
      }
      setStoredValue(readValue())
    }
    window.addEventListener('storage', handleStorageChange)

    return () => window.removeEventListener('storage', handleStorageChange)
  }, [key, readValue])

  // Listen for changes from the same tab (custom local-storage event)
  useEffect(() => {
    const handleStorageChange = (event: LocalStorageCustomEvent) => {
      if (event.detail.key !== key) {
        return
      }
      setStoredValue(readValue())
    }
    window.addEventListener('local-storage', handleStorageChange)

    return () =>
      window.removeEventListener('local-storage', handleStorageChange)
  }, [key, readValue])

  return [storedValue, setValue, removeValue]
}
